import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  Tabs,
  Tab,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  CircularProgress,
  TablePagination
} from '@mui/material';
import {
  Edit as EditIcon,
  Visibility as PreviewIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  History as LogsIcon,
  Send as SendIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import apiService from '../../services/api';
import axios from 'axios';

// Create a fresh axios instance to avoid header conflicts
const notificationAxios = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000/api'
});

// API wrapper functions
const notificationAPI = {
  getConfigs: async (token) => {
    try {
      const response = await notificationAxios.get('/admin/notifications/configs', {
        headers: {
          'x-auth-token': token
        }
      });
      return response.data;
    } catch (error) {
      console.error('getConfigs error:', error.response?.status, error.response?.data);
      throw error;
    }
  },
  
  updateConfig: async (eventType, data, token) => {
    const response = await notificationAxios.put(`/admin/notifications/configs/${eventType}`, data, {
      headers: { 'x-auth-token': token }
    });
    return response.data;
  },
  
  sendTest: async (data, token) => {
    const response = await notificationAxios.post('/admin/notifications/test', data, {
      headers: { 'x-auth-token': token }
    });
    return response.data;
  },
  
  getLogs: async (token, params = {}) => {
    const response = await notificationAxios.get('/admin/notifications/logs', {
      headers: { 'x-auth-token': token },
      params: params // Add query parameters support
    });
    return response.data;
  }
};

const NotificationConfigManager = ({ token }) => {
  console.log('NotificationConfigManager - received token:', typeof token, token);
  
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  
  // Dialog states
  const [editDialog, setEditDialog] = useState({ open: false, config: null });
  const [previewDialog, setPreviewDialog] = useState({ open: false, config: null });
  const [testDialog, setTestDialog] = useState({ open: false, config: null });
  const [logsDialog, setLogsDialog] = useState({ open: false });
  
  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    loadConfigs();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadConfigs = async () => {
    try {
      setLoading(true);
      console.log('loadConfigs - token:', token ? `${token.substring(0, 20)}...` : 'No token');
      const data = await notificationAPI.getConfigs(token);
      setConfigs(data);
      setError(null);
    } catch (err) {
      setError('Failed to load notification configurations');
      console.error('loadConfigs error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (config) => {
    try {
      const updatedConfig = {
        ...config,
        enabled: !config.enabled
      };
      
      await notificationAPI.updateConfig(config.eventType, updatedConfig, token);
      
      setConfigs(configs.map(c => 
        c.eventType === config.eventType ? updatedConfig : c
      ));
      
      setSuccessMessage(`${config.eventType} ${updatedConfig.enabled ? 'enabled' : 'disabled'}`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError('Failed to update configuration');
      console.error(err);
    }
  };

  const handleEdit = (config) => {
    setEditDialog({ open: true, config: { ...config } });
  };

  const handlePreview = (config) => {
    setPreviewDialog({ open: true, config });
  };

  const handleTest = (config) => {
    setTestDialog({ open: true, config });
  };

  const getChannelIcon = (type) => {
    switch (type) {
      case 'email':
        return <EmailIcon fontSize="small" />;
      case 'sms':
        return <SmsIcon fontSize="small" />;
      default:
        return null;
    }
  };

  const getEventTypeColor = (eventType) => {
    if (eventType.includes('CRISIS') || eventType.includes('SAFETY')) return 'error';
    if (eventType.includes('THERAPIST')) return 'primary';
    if (eventType.includes('REMINDER')) return 'info';
    return 'default';
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Notification Configuration
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}

      <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 3 }}>
        <Tab label="Configurations" />
        <Tab label="Templates" />
        <Tab label="Logs" icon={<LogsIcon />} />
        <Tab label="Test" icon={<SendIcon />} />
      </Tabs>

      {activeTab === 0 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Event Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Channels</TableCell>
                <TableCell>Rate Limit</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {configs.map((config) => (
                <TableRow key={config.eventType}>
                  <TableCell>
                    <Box>
                      <Typography variant="subtitle2">
                        {config.eventType.replace(/_/g, ' ')}
                      </Typography>
                      <Chip 
                        label={config.eventType}
                        size="small"
                        color={getEventTypeColor(config.eventType)}
                        sx={{ mt: 0.5 }}
                      />
                    </Box>
                  </TableCell>
                  
                  <TableCell>
                    <Switch
                      checked={config.enabled}
                      onChange={() => handleToggle(config)}
                      color="primary"
                    />
                  </TableCell>
                  
                  <TableCell>
                    <Box display="flex" gap={0.5}>
                      {config.channels?.map((channel) => (
                        <Chip
                          key={channel.type}
                          icon={getChannelIcon(channel.type)}
                          label={channel.type}
                          size="small"
                          variant={channel.enabled ? 'filled' : 'outlined'}
                          color={channel.enabled ? 'primary' : 'default'}
                        />
                      ))}
                    </Box>
                  </TableCell>
                  
                  <TableCell>
                    <Typography variant="caption">
                      {config.rateLimiting?.maxPerUser?.daily || 'No limit'}/day
                    </Typography>
                  </TableCell>
                  
                  <TableCell align="right">
                    <IconButton 
                      size="small" 
                      onClick={() => handleEdit(config)}
                      title="Edit Configuration"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      onClick={() => handlePreview(config)}
                      title="Preview Template"
                    >
                      <PreviewIcon />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      onClick={() => handleTest(config)}
                      title="Send Test"
                    >
                      <SendIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {activeTab === 1 && (
        <TemplateManager configs={configs} onUpdate={loadConfigs} token={token} />
      )}

      {activeTab === 2 && (
        <NotificationLogs token={token} />
      )}

      {activeTab === 3 && (
        <TestNotificationPanel token={token} />
      )}

      {/* Edit Configuration Dialog */}
      <EditConfigDialog
        open={editDialog.open}
        config={editDialog.config}
        onClose={() => setEditDialog({ open: false, config: null })}
        onSave={async (updatedConfig) => {
          try {
            await notificationAPI.updateConfig(updatedConfig.eventType, updatedConfig, token);
            await loadConfigs();
            setEditDialog({ open: false, config: null });
            setSuccessMessage('Configuration updated successfully');
          } catch (err) {
            setError('Failed to save configuration');
          }
        }}
      />

      {/* Preview Dialog */}
      <PreviewDialog
        open={previewDialog.open}
        config={previewDialog.config}
        onClose={() => setPreviewDialog({ open: false, config: null })}
      />

      {/* Test Notification Dialog */}
      <TestNotificationDialog
        open={testDialog.open}
        config={testDialog.config}
        onClose={() => setTestDialog({ open: false, config: null })}
        onTest={async (testData) => {
          try {
            await notificationAPI.sendTest({
              eventType: testDialog.config.eventType,
              ...testData
            }, token);
            setTestDialog({ open: false, config: null });
            setSuccessMessage('Test notification sent successfully');
          } catch (err) {
            setError('Failed to send test notification');
          }
        }}
      />
    </Box>
  );
};

// Template Manager Component
const TemplateManager = ({ configs, onUpdate, token }) => {
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [editedTemplate, setEditedTemplate] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleSelectConfig = (config) => {
    setSelectedConfig(config);
    setEditedTemplate(config.templates?.email ? { ...config.templates.email } : {});
  };

  const handleSaveTemplate = async () => {
    if (!selectedConfig || !editedTemplate) return;

    try {
      setSaving(true);
      const updatedConfig = {
        ...selectedConfig,
        templates: {
          ...selectedConfig.templates,
          email: editedTemplate
        }
      };
      
      await notificationAPI.updateConfig(selectedConfig.eventType, updatedConfig, token);
      await onUpdate();
      setSaving(false);
    } catch (err) {
      console.error('Failed to save template:', err);
      setSaving(false);
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={4}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Select Notification Type
          </Typography>
          {configs.map((config) => (
            <Button
              key={config.eventType}
              fullWidth
              variant={selectedConfig?.eventType === config.eventType ? 'contained' : 'outlined'}
              sx={{ mb: 1, justifyContent: 'flex-start' }}
              onClick={() => handleSelectConfig(config)}
            >
              {config.eventType.replace(/_/g, ' ')}
            </Button>
          ))}
        </Paper>
      </Grid>
      
      <Grid item xs={12} md={8}>
        {selectedConfig && editedTemplate && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Edit Email Template: {selectedConfig.eventType}
            </Typography>
            
            <TextField
              fullWidth
              label="Subject"
              value={editedTemplate.subject || ''}
              onChange={(e) => setEditedTemplate({ ...editedTemplate, subject: e.target.value })}
              margin="normal"
            />
            
            <TextField
              fullWidth
              multiline
              rows={6}
              label="HTML Body"
              value={editedTemplate.bodyHtml || ''}
              onChange={(e) => setEditedTemplate({ ...editedTemplate, bodyHtml: e.target.value })}
              margin="normal"
            />
            
            <TextField
              fullWidth
              multiline
              rows={6}
              label="Text Body"
              value={editedTemplate.bodyText || ''}
              onChange={(e) => setEditedTemplate({ ...editedTemplate, bodyText: e.target.value })}
              margin="normal"
            />
            
            <Box mt={2}>
              <Typography variant="subtitle2" gutterBottom>
                Available Variables:
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {selectedConfig.templates?.email?.variables?.map((variable) => (
                  <Chip
                    key={variable}
                    label={`{{${variable}}}`}
                    size="small"
                    onClick={() => navigator.clipboard.writeText(`{{${variable}}}`)}
                  />
                ))}
              </Box>
            </Box>
            
            <Box mt={3} display="flex" gap={2}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveTemplate}
                disabled={saving}
              >
                Save Template
              </Button>
              <Button
                variant="outlined"
                onClick={() => setEditedTemplate({ ...selectedConfig.templates.email })}
              >
                Reset
              </Button>
            </Box>
          </Paper>
        )}
      </Grid>
    </Grid>
  );
};

// Notification Logs Component (Enhanced with Pagination & Filtering)
const NotificationLogs = ({ token }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalLogsCount, setTotalLogsCount] = useState(0);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [filters, setFilters] = useState({
    eventType: '',
    status: '',
    channel: '',
    startDate: '',
    endDate: ''
  });

  useEffect(() => {
    loadLogs();
  }, [page, rowsPerPage, filters, refreshTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadLogs = async () => {
    try {
      setLoading(true);

      // Build query parameters
      const params = {
        limit: rowsPerPage,
        skip: page * rowsPerPage
      };

      // Add filters if set
      if (filters.eventType) params.eventType = filters.eventType;
      if (filters.status) params.status = filters.status;
      if (filters.channel) params.channel = filters.channel;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;

      const data = await notificationAPI.getLogs(token, params);
      setLogs(data.logs || []);
      setTotalLogsCount(data.total || 0);
    } catch (err) {
      console.error('Failed to load logs:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(0); // Reset to first page when filter changes
  };

  const handleClearFilters = () => {
    setFilters({
      eventType: '',
      status: '',
      channel: '',
      startDate: '',
      endDate: ''
    });
    setPage(0);
  };

  const handleRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'sent':
      case 'delivered':
        return 'success';
      case 'failed':
      case 'bounced':
        return 'error';
      case 'queued':
      case 'processing':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getEventTypeColor = (eventType) => {
    if (eventType.includes('CRISIS') || eventType.includes('SAFETY')) return 'error';
    if (eventType.includes('THERAPIST')) return 'primary';
    if (eventType.includes('REMINDER')) return 'info';
    return 'default';
  };

  return (
    <Box>
      {/* Filters Section */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Filters</Typography>
          <Box>
            <Button
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              sx={{ mr: 1 }}
            >
              Refresh
            </Button>
            <Button
              onClick={handleClearFilters}
              variant="outlined"
            >
              Clear Filters
            </Button>
          </Box>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Event Type</InputLabel>
              <Select
                value={filters.eventType}
                label="Event Type"
                onChange={(e) => handleFilterChange('eventType', e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="CRISIS_ALERT">Crisis Alert</MenuItem>
                <MenuItem value="THERAPIST_MESSAGE">Therapist Message</MenuItem>
                <MenuItem value="ASSESSMENT_DUE">Assessment Due</MenuItem>
                <MenuItem value="APPOINTMENT_REMINDER">Appointment Reminder</MenuItem>
                <MenuItem value="DAILY_CHECK_IN">Daily Check-In</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                label="Status"
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="sent">Sent</MenuItem>
                <MenuItem value="delivered">Delivered</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
                <MenuItem value="bounced">Bounced</MenuItem>
                <MenuItem value="queued">Queued</MenuItem>
                <MenuItem value="processing">Processing</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Channel</InputLabel>
              <Select
                value={filters.channel}
                label="Channel"
                onChange={(e) => handleFilterChange('channel', e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="email">Email</MenuItem>
                <MenuItem value="sms">SMS</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2.5}>
            <TextField
              fullWidth
              size="small"
              label="Start Date"
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2.5}>
            <TextField
              fullWidth
              size="small"
              label="End Date"
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Logs Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Time</TableCell>
              <TableCell>Event Type</TableCell>
              <TableCell>Channel</TableCell>
              <TableCell>Recipient</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Error</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : logs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No notification logs found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              logs.map((log) => (
                <TableRow key={log._id}>
                  <TableCell>
                    {new Date(log.createdAt).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={log.eventType}
                      size="small"
                      color={getEventTypeColor(log.eventType)}
                    />
                  </TableCell>
                  <TableCell>{log.channel}</TableCell>
                  <TableCell>
                    {log.recipient?.email || log.recipient?.phone || 'N/A'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={log.status}
                      size="small"
                      color={getStatusColor(log.status)}
                    />
                  </TableCell>
                  <TableCell>
                    {log.lastError && (
                      <Typography variant="caption" color="error">
                        {log.lastError}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalLogsCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
    </Box>
  );
};

// Edit Configuration Dialog
const EditConfigDialog = ({ open, config, onClose, onSave }) => {
  const [editedConfig, setEditedConfig] = useState(null);

  useEffect(() => {
    if (config) {
      setEditedConfig({ ...config });
    }
  }, [config]);

  const getChannelIcon = (type) => {
    switch (type) {
      case 'email':
        return <EmailIcon fontSize="small" />;
      case 'sms':
        return <SmsIcon fontSize="small" />;
      default:
        return null;
    }
  };

  if (!editedConfig) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit Configuration: {editedConfig.eventType}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <FormControl fullWidth margin="normal">
            <InputLabel>Status</InputLabel>
            <Select
              value={editedConfig.enabled ? 'enabled' : 'disabled'}
              onChange={(e) => setEditedConfig({ ...editedConfig, enabled: e.target.value === 'enabled' })}
            >
              <MenuItem value="enabled">Enabled</MenuItem>
              <MenuItem value="disabled">Disabled</MenuItem>
            </Select>
          </FormControl>

          <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
            Channels
          </Typography>
          {editedConfig.channels?.map((channel, index) => (
            <Box key={channel.type} sx={{ mb: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4}>
                  <Typography variant="subtitle2">
                    {getChannelIcon(channel.type)} {channel.type.toUpperCase()}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={channel.enabled ? 'enabled' : 'disabled'}
                      onChange={(e) => {
                        const newChannels = [...editedConfig.channels];
                        newChannels[index].enabled = e.target.value === 'enabled';
                        setEditedConfig({ ...editedConfig, channels: newChannels });
                      }}
                    >
                      <MenuItem value="enabled">Enabled</MenuItem>
                      <MenuItem value="disabled">Disabled</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Priority"
                    type="number"
                    value={channel.priority || 1}
                    onChange={(e) => {
                      const newChannels = [...editedConfig.channels];
                      newChannels[index].priority = parseInt(e.target.value);
                      setEditedConfig({ ...editedConfig, channels: newChannels });
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
          ))}

          <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
            Rate Limiting
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Max per hour"
                type="number"
                value={editedConfig.rateLimiting?.maxPerUser?.hourly || ''}
                onChange={(e) => setEditedConfig({
                  ...editedConfig,
                  rateLimiting: {
                    ...editedConfig.rateLimiting,
                    maxPerUser: {
                      ...editedConfig.rateLimiting?.maxPerUser,
                      hourly: parseInt(e.target.value) || 0
                    }
                  }
                })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Max per day"
                type="number"
                value={editedConfig.rateLimiting?.maxPerUser?.daily || ''}
                onChange={(e) => setEditedConfig({
                  ...editedConfig,
                  rateLimiting: {
                    ...editedConfig.rateLimiting,
                    maxPerUser: {
                      ...editedConfig.rateLimiting?.maxPerUser,
                      daily: parseInt(e.target.value) || 0
                    }
                  }
                })}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={() => onSave(editedConfig)} variant="contained" color="primary">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Preview Dialog
const PreviewDialog = ({ open, config, onClose }) => {
  const [previewData, setPreviewData] = useState({
    clientName: 'John Doe',
    therapistName: 'Dr. Smith',
    timestamp: new Date().toLocaleString(),
    triggerPhrase: 'Example trigger phrase',
    detectedContext: 'crisis_situation',
    conversationLink: 'https://app.aimhi.health/conversation/123',
    assessmentType: 'PHQ-9',
    assessmentLink: 'https://app.aimhi.health/assessment/123',
    checkInLink: 'https://app.aimhi.health/checkin'
  });

  const renderTemplate = (template, data) => {
    if (!template) return '';
    let result = template;
    Object.entries(data).forEach(([key, value]) => {
      result = result.replace(new RegExp(`{{${key}}}`, 'g'), value);
    });
    return result;
  };

  if (!config) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Preview: {config.eventType}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Typography variant="h6" gutterBottom>
            Test Data
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {Object.entries(previewData).map(([key, value]) => (
              <Grid item xs={12} sm={6} key={key}>
                <TextField
                  fullWidth
                  size="small"
                  label={key}
                  value={value}
                  onChange={(e) => setPreviewData({ ...previewData, [key]: e.target.value })}
                />
              </Grid>
            ))}
          </Grid>

          <Divider sx={{ my: 3 }} />

          {config.templates?.email && (
            <>
              <Typography variant="h6" gutterBottom>
                Email Preview
              </Typography>
              <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.100' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Subject: {renderTemplate(config.templates.email.subject, previewData)}
                </Typography>
              </Paper>
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  HTML Body:
                </Typography>
                <Box 
                  dangerouslySetInnerHTML={{ 
                    __html: renderTemplate(config.templates.email.bodyHtml, previewData) 
                  }}
                  sx={{ '& a': { color: 'primary.main' } }}
                />
              </Paper>
              <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Text Body:
                </Typography>
                <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
                  {renderTemplate(config.templates.email.bodyText, previewData)}
                </Typography>
              </Paper>
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

// Test Notification Dialog
const TestNotificationDialog = ({ open, config, onClose, onTest }) => {
  const [testData, setTestData] = useState({
    recipientEmail: '',
    recipientPhone: ''
  });

  const handleSubmit = () => {
    if (!testData.recipientEmail) {
      alert('Please enter a recipient email');
      return;
    }
    onTest(testData);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Send Test: {config?.eventType}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <TextField
            fullWidth
            label="Recipient Email"
            type="email"
            value={testData.recipientEmail}
            onChange={(e) => setTestData({ ...testData, recipientEmail: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Recipient Phone (optional)"
            value={testData.recipientPhone}
            onChange={(e) => setTestData({ ...testData, recipientPhone: e.target.value })}
            margin="normal"
            placeholder="+1234567890"
          />
          <Alert severity="info" sx={{ mt: 2 }}>
            This will send a test notification with sample data to the specified recipient.
          </Alert>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" color="primary">
          Send Test
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Test Notification Panel Component
const TestNotificationPanel = ({ token }) => {
  const [loading, setLoading] = useState(false);
  const [testType, setTestType] = useState('crisis');
  const [targetUserId, setTargetUserId] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [users, setUsers] = useState([]);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await apiService.getUsersNoPaging(token);
      // Filter to show only users with therapist role (they're more likely to have phone numbers)
      const therapists = response.filter(u => u.role.includes('therapist'));
      setUsers(therapists);
    } catch (err) {
      console.error('Failed to load users:', err);
    }
  };

  const handleTestNotification = async () => {
    setLoading(true);
    setError('');
    setResult(null);

    try {
      const response = await apiService.testNotification(token, {
        type: testType,
        targetUserId
      });
      
      setResult(response);
    } catch (err) {
      setError(err.message || 'Failed to send test notification');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Test Notifications
      </Typography>
      
      <Alert severity="info" sx={{ mb: 3 }}>
        Use this to test notification delivery. In development, mock providers will log to the server console. 
        Make sure the target user has a phone number for SMS tests.
      </Alert>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Notification Type</InputLabel>
            <Select
              value={testType}
              onChange={(e) => setTestType(e.target.value)}
              label="Notification Type"
            >
              <MenuItem value="crisis">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Alert severity="error" icon={false} sx={{ p: 0 }}>🚨</Alert>
                  Crisis Alert (Email + SMS)
                </Box>
              </MenuItem>
              <MenuItem value="email">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EmailIcon />
                  Email Only
                </Box>
              </MenuItem>
              <MenuItem value="sms">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SmsIcon />
                  SMS Only
                </Box>
              </MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Target User</InputLabel>
            <Select
              value={targetUserId}
              onChange={(e) => setTargetUserId(e.target.value)}
              label="Target User"
            >
              <MenuItem value="">
                <em>Select a user</em>
              </MenuItem>
              {users.map(user => (
                <MenuItem key={user._id} value={user._id}>
                  {user.name || user.email} 
                  {user.phone && ` (${user.phone})`}
                  {!user.phone && testType !== 'email' && ' (No phone)'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleTestNotification}
            disabled={loading || !targetUserId}
            startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
            fullWidth
          >
            {loading ? 'Sending...' : 'Send Test Notification'}
          </Button>
        </Grid>
      </Grid>

      {error && (
        <Alert severity="error" sx={{ mt: 3 }}>
          {error}
        </Alert>
      )}

      {result && (
        <Alert severity="success" sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Test notification sent successfully!
          </Typography>
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2">
              Type: {result.type}
            </Typography>
            <Typography variant="body2">
              Target: {result.targetUser.email} 
              {result.targetUser.phone && ` (${result.targetUser.phone})`}
            </Typography>
            {result.result && result.result.results && (
              <Box sx={{ mt: 1 }}>
                {result.result.results.map((channel, idx) => (
                  <Typography key={idx} variant="body2">
                    • {channel.channel}: {channel.success ? '✓ Sent' : '✗ Failed'}
                    {!channel.success && ` - ${channel.error}`}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        </Alert>
      )}
    </Paper>
  );
};

export default NotificationConfigManager;